import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useVideoCall } from '../../context/VideoCallContext';

// Minimized bubble dimensions (shows both video streams)
const MINI_W = 150;
const MINI_H = 110;
// Local PiP in full-screen view
const PIP_W = 180;
const PIP_H = 130;
// How much of the PiP stays visible when snapped to an edge (px)
const PIP_PEEK = Math.round(PIP_W * 0.10);

const HangUpIcon = ({ size = 26 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
    <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"
      transform="rotate(135,12,12)"/>
  </svg>
);

export const VideoCallWidget: React.FC = () => {
  const {
    callState, endReason, incomingCaller, remoteStream, localStream,
    acceptCall, rejectCall, hangUp
  } = useVideoCall();

  const [minimized, setMinimized] = useState(false);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);

  // Minimized bubble drag
  const [bubblePos, setBubblePos] = useState({
    x: window.innerWidth - MINI_W - 16,
    y: window.innerHeight - MINI_H - 80,
  });
  const bubbleDragging = useRef(false);
  const bubbleMoved = useRef(false);
  const bubbleDragStart = useRef({ mx: 0, my: 0, ox: 0, oy: 0 });

  // Local PiP drag (fullscreen view) — can slide 90% off either edge
  const [pipPos, setPipPos] = useState({
    x: window.innerWidth - PIP_W - 16,
    y: window.innerHeight - PIP_H - 80,
  });
  const pipDragging = useRef(false);
  const pipDragStart = useRef({ mx: 0, my: 0, ox: 0, oy: 0 });

  useEffect(() => {
    if (callState === 'connected') {
      setMinimized(false);
      setMuted(false);
      setCameraOff(false);
      setBubblePos({ x: window.innerWidth - MINI_W - 16, y: window.innerHeight - MINI_H - 80 });
      setPipPos({ x: window.innerWidth - PIP_W - 16, y: window.innerHeight - PIP_H - 80 });
    }
  }, [callState]);

  const remoteVideoRef    = useCallback((el: HTMLVideoElement | null) => { if (el && remoteStream) el.srcObject = remoteStream; }, [remoteStream]);
  const localVideoRef     = useCallback((el: HTMLVideoElement | null) => { if (el && localStream)  el.srcObject = localStream;  }, [localStream]);
  const miniRemoteRef     = useCallback((el: HTMLVideoElement | null) => { if (el && remoteStream) el.srcObject = remoteStream; }, [remoteStream]);
  const miniLocalRef      = useCallback((el: HTMLVideoElement | null) => { if (el && localStream)  el.srcObject = localStream;  }, [localStream]);

  useEffect(() => { localStream?.getAudioTracks().forEach(t => { t.enabled = !muted; }); }, [muted, localStream]);
  useEffect(() => { localStream?.getVideoTracks().forEach(t => { t.enabled = !cameraOff; }); }, [cameraOff, localStream]);

  // Snap the PiP to an edge if dragged within 30% of screen width from that edge
  const snapPip = (x: number, y: number) => {
    const W = window.innerWidth;
    const H = window.innerHeight;
    let nx = x, ny = Math.max(0, Math.min(y, H - PIP_H));
    if (x + PIP_W / 2 < W * 0.30) {
      nx = -PIP_W + PIP_PEEK; // snap left — only PIP_PEEK px visible
    } else if (x + PIP_W / 2 > W * 0.70) {
      nx = W - PIP_PEEK;      // snap right — only PIP_PEEK px visible
    } else {
      nx = Math.max(-PIP_W + PIP_PEEK, Math.min(x, W - PIP_PEEK));
    }
    setPipPos({ x: nx, y: ny });
  };

  useEffect(() => {
    const getXY = (e: MouseEvent | TouchEvent) => 'touches' in e
      ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
      : { x: e.clientX, y: e.clientY };

    const onMove = (e: MouseEvent | TouchEvent) => {
      const { x, y } = getXY(e);
      if (bubbleDragging.current) {
        bubbleMoved.current = true;
        const nx = bubbleDragStart.current.ox + (x - bubbleDragStart.current.mx);
        const ny = bubbleDragStart.current.oy + (y - bubbleDragStart.current.my);
        setBubblePos({
          x: Math.max(0, Math.min(nx, window.innerWidth  - MINI_W)),
          y: Math.max(0, Math.min(ny, window.innerHeight - MINI_H)),
        });
      }
      if (pipDragging.current) {
        const nx = pipDragStart.current.ox + (x - pipDragStart.current.mx);
        const ny = pipDragStart.current.oy + (y - pipDragStart.current.my);
        // Allow free off-screen movement while dragging — snap on release
        setPipPos({
          x: Math.max(-(PIP_W - PIP_PEEK), Math.min(nx, window.innerWidth  - PIP_PEEK)),
          y: Math.max(0,                    Math.min(ny, window.innerHeight - PIP_H)),
        });
      }
    };

    const onUp = () => {
      if (pipDragging.current) {
        // Snap to edge on release
        setPipPos(prev => {
          const snapped = { x: prev.x, y: prev.y };
          const W = window.innerWidth;
          if (prev.x + PIP_W / 2 < W * 0.30)      snapped.x = -PIP_W + PIP_PEEK;
          else if (prev.x + PIP_W / 2 > W * 0.70)  snapped.x = W - PIP_PEEK;
          return snapped;
        });
      }
      bubbleDragging.current = false;
      pipDragging.current    = false;
    };

    window.addEventListener('mousemove',  onMove);
    window.addEventListener('mouseup',    onUp);
    window.addEventListener('touchmove',  onMove, { passive: false });
    window.addEventListener('touchend',   onUp);
    return () => {
      window.removeEventListener('mousemove',  onMove);
      window.removeEventListener('mouseup',    onUp);
      window.removeEventListener('touchmove',  onMove);
      window.removeEventListener('touchend',   onUp);
    };
  }, []);

  const onBubbleDown = (e: React.MouseEvent | React.TouchEvent) => {
    const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const y = 'touches' in e ? e.touches[0].clientY : e.clientY;
    bubbleDragging.current = true;
    bubbleMoved.current    = false;
    bubbleDragStart.current = { mx: x, my: y, ox: bubblePos.x, oy: bubblePos.y };
    e.stopPropagation();
  };

  const onPipDown = (e: React.MouseEvent | React.TouchEvent) => {
    const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const y = 'touches' in e ? e.touches[0].clientY : e.clientY;
    pipDragging.current = true;
    pipDragStart.current = { mx: x, my: y, ox: pipPos.x, oy: pipPos.y };
    e.stopPropagation();
    e.preventDefault();
  };

  // ── Incoming call ──────────────────────────────────────────────────────────
  if (callState === 'ringing' && incomingCaller) {
    return (
      <div style={S.overlay}>
        <div style={S.incomingCard}>
          <div style={S.avatar}>
            {incomingCaller.avatar
              ? <img src={incomingCaller.avatar} alt="" style={S.avatarImg} />
              : <span style={S.avatarInitial}>{incomingCaller.name[0]?.toUpperCase()}</span>}
          </div>
          <p style={S.callerName}>{incomingCaller.name}</p>
          <p style={S.callerSub}>Incoming video call…</p>
          <div style={S.callActions}>
            <button onClick={rejectCall} style={S.answerBtnRed}  aria-label="Decline"><HangUpIcon /></button>
            <button onClick={acceptCall} style={S.answerBtnGreen} aria-label="Accept">
              <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
                <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Outgoing / waiting ─────────────────────────────────────────────────────
  if (callState === 'calling') {
    return (
      <div style={S.overlay}>
        <div style={S.incomingCard}>
          <p style={S.callerName}>Calling…</p>
          <p style={S.callerSub}>Waiting for the other person to pick up</p>
          <button onClick={hangUp} style={{ ...S.answerBtnRed, marginTop: 20 }} aria-label="Cancel"><HangUpIcon /></button>
        </div>
      </div>
    );
  }

  // ── Ended overlay ──────────────────────────────────────────────────────────
  if (callState === 'ended') {
    const msgs: Record<string, string> = {
      rejected: 'Call declined', busy: 'User is on another call',
      unavailable: 'User is not available', error: 'Call failed — check camera & microphone access',
      hangup: 'Call ended',
    };
    return (
      <div style={S.overlay}>
        <div style={S.incomingCard}>
          <p style={S.callerName}>{msgs[endReason ?? 'hangup'] ?? 'Call ended'}</p>
        </div>
      </div>
    );
  }

  if (callState !== 'connected') return null;

  // ── Minimized bubble — shows BOTH streams ──────────────────────────────────
  if (minimized) {
    return (
      <div
        style={{ ...S.miniBubble, left: bubblePos.x, top: bubblePos.y }}
        onMouseDown={onBubbleDown}
        onTouchStart={onBubbleDown}
        onMouseUp={() => { if (!bubbleMoved.current) setMinimized(false); }}
        onTouchEnd={() => { if (!bubbleMoved.current) setMinimized(false); }}
        title="Tap to expand"
      >
        {/* Remote fills bubble */}
        <video ref={miniRemoteRef} autoPlay playsInline style={S.miniRemote} />
        {/* Local small overlay — bottom-right */}
        <video ref={miniLocalRef} autoPlay playsInline muted style={S.miniLocal} />
        {/* Hang-up button */}
        <button
          onMouseDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); hangUp(); }}
          style={S.miniHangup}
          aria-label="End call"
        >
          <HangUpIcon size={12} />
        </button>
      </div>
    );
  }

  // ── Full-screen connected call ─────────────────────────────────────────────
  return (
    <div style={S.fullscreen}>
      <video ref={remoteVideoRef} autoPlay playsInline style={S.remoteVideo} />

      {/* Local PiP — slides 90% off-screen to either edge */}
      <video
        ref={localVideoRef}
        autoPlay playsInline muted
        onMouseDown={onPipDown}
        onTouchStart={onPipDown}
        style={{ ...S.localVideo, left: pipPos.x, top: pipPos.y, cursor: 'grab' }}
        title="Drag to edge to tuck away"
      />

      <div style={S.topBar}>
        <span style={S.callLabel}>🔒 Encrypted Call</span>
        <button onClick={() => setMinimized(true)} style={S.topBtn} title="Minimize">
          <svg viewBox="0 0 24 24" fill="white" width="20" height="20">
            <path d="M20 14H4v-2h16v2z"/>
          </svg>
        </button>
      </div>

      <div style={S.controls}>
        <button
          onClick={() => setMuted(m => !m)}
          style={{ ...S.ctrlBtn, background: muted ? '#ff3b30' : 'rgba(255,255,255,0.2)' }}
          title={muted ? 'Unmute' : 'Mute'}
        >
          {muted
            ? <svg viewBox="0 0 24 24" fill="white" width="24" height="24"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/></svg>
            : <svg viewBox="0 0 24 24" fill="white" width="24" height="24"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.49 6-3.31 6-6.72h-1.7z"/></svg>
          }
        </button>
        <button
          onClick={() => setCameraOff(c => !c)}
          style={{ ...S.ctrlBtn, background: cameraOff ? '#ff3b30' : 'rgba(255,255,255,0.2)' }}
          title={cameraOff ? 'Camera on' : 'Camera off'}
        >
          {cameraOff
            ? <svg viewBox="0 0 24 24" fill="white" width="24" height="24"><path d="M21 6.5l-4-4-15 15 1.41 1.41 2.39-2.38A2 2 0 0 0 6 19h12a2 2 0 0 0 2-2V8l4 4V6.5zM7.42 8H16l-8.58 8.58V8zM2 6l2 2V5h10.17L12 3H4a2 2 0 0 0-2 2v11l2 2V6z"/></svg>
            : <svg viewBox="0 0 24 24" fill="white" width="24" height="24"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>
          }
        </button>
        <button onClick={hangUp} style={S.hangUpBtn} title="End call">
          <HangUpIcon size={28} />
        </button>
      </div>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
  },
  incomingCard: {
    background: '#1a1a2e', borderRadius: 20, padding: '36px 48px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
  },
  avatar: {
    width: 80, height: 80, borderRadius: '50%', overflow: 'hidden',
    background: '#4a9eff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8,
  },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
  avatarInitial: { color: '#fff', fontSize: 32, fontWeight: 700 },
  callerName: { color: '#fff', fontSize: 22, fontWeight: 600, margin: 0 },
  callerSub: { color: '#aaa', fontSize: 14, margin: '4px 0 20px' },
  callActions: { display: 'flex', gap: 24 },
  answerBtnRed: {
    width: 68, height: 68, borderRadius: '50%', background: '#ff3b30',
    border: 'none', cursor: 'pointer', color: 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(255,59,48,0.5)',
  },
  answerBtnGreen: {
    width: 68, height: 68, borderRadius: '50%', background: '#34c759',
    border: 'none', cursor: 'pointer', color: 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(52,199,89,0.5)',
  },
  // Minimized bubble — rounded rectangle showing both streams
  miniBubble: {
    position: 'fixed', zIndex: 9998,
    width: MINI_W, height: MINI_H, borderRadius: 14,
    overflow: 'hidden', cursor: 'grab',
    boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
    userSelect: 'none', background: '#111',
    border: '2px solid rgba(255,255,255,0.15)',
  },
  miniRemote: {
    position: 'absolute', inset: 0,
    width: '100%', height: '100%', objectFit: 'cover',
  },
  miniLocal: {
    position: 'absolute', bottom: 6, right: 6,
    width: 48, height: 36, objectFit: 'cover',
    borderRadius: 6, border: '1.5px solid rgba(255,255,255,0.5)',
    background: '#333',
  },
  miniHangup: {
    position: 'absolute', top: 6, right: 6,
    width: 24, height: 24, borderRadius: '50%',
    background: '#ff3b30', border: 'none', color: 'white',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  fullscreen: { position: 'fixed', inset: 0, zIndex: 9998, background: '#000' },
  remoteVideo: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' },
  localVideo: {
    position: 'fixed', zIndex: 9999,
    width: PIP_W, height: PIP_H, objectFit: 'cover',
    borderRadius: 12, border: '2px solid rgba(255,255,255,0.3)',
    background: '#111', userSelect: 'none',
    transition: 'left 0.25s ease, top 0.1s ease',
  },
  topBar: {
    position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px',
    background: 'linear-gradient(rgba(0,0,0,0.55), transparent)',
  },
  callLabel: { color: 'rgba(255,255,255,0.85)', fontSize: 14, fontWeight: 600 },
  topBtn: {
    background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8,
    padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center',
  },
  controls: {
    position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2,
    display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 18,
    padding: '18px 0 24px',
    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
  },
  ctrlBtn: {
    border: 'none', borderRadius: '50%', width: 56, height: 56,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  hangUpBtn: {
    border: 'none', borderRadius: '50%', width: 68, height: 68,
    background: '#ff3b30', cursor: 'pointer', color: 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(255,59,48,0.5)',
  },
};
